import React, { useState } from 'react';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { Delete, ViewList, ViewStream, Add } from '@material-ui/icons';

import Spec, { SpecData } from '../../../models/Spec';
import { TestCasePanel } from '../../ExpansionPanel';
import { TestCaseForm } from '../../Form';
import { TestCaseInputChange } from '../../../types/HTMLPropsTypes';

interface Props {
  spec: Spec
  onSpecChange?: (spec: Spec) => void
}

const Simple = (props: Props) => {
  const { spec, onSpecChange = () => {} } = props;
  const [expandedTestCases, setExpandedTestCases] = useState([true] as boolean[]);

  // sets test case value
  const setTestCase = (index: number, data: SpecData) => {
    const _spec = new Spec(spec);
    _spec.data[index] = data;
    onSpecChange(_spec);
  };

  // sets value to the given field of test case
  const setTestCaseField: TestCaseInputChange = (index, field, value) => {
    const _spec = new Spec(spec);
    if (field === 'arguments') {
      _spec.data[index]!.arguments.push(value);
      _spec.data[index]!.mainInvoker = Spec.getTestCaseMainInvoker(_spec.data[index]);
    } else {
      _spec.data[index]![field] = value;
    }
    onSpecChange(_spec);
  };

  // adds new test case
  const addTestCase = () => {
    const _spec = new Spec(spec);
    _spec.addItem();
    onSpecChange(_spec);
  };

  // removes test case
  const removeTestCase = (index: number) => {
    const _spec = new Spec(spec);
    _spec.data.splice(index, 1);
    onSpecChange(_spec);
  };

  // removes test case input/argument
  const removeTestCaseInput = (testCaseIndex: number, inputIndex: number) => {
    const _spec = new Spec(spec);
    _spec.data[testCaseIndex].arguments.splice(inputIndex, 1);
    _spec.data[testCaseIndex]!.mainInvoker = Spec.getTestCaseMainInvoker(_spec.data[testCaseIndex]);
    onSpecChange(_spec);
  };

  // flag if all test cases are expanded
  const isAllExpanded = spec.data.every((a, i) => expandedTestCases[i]);

  return (
    <Grid container spacing={2}>
      {spec.data.length > 1 && (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            {isAllExpanded && (
              <Button
                color="primary"
                size="small"
                onClick={() => setExpandedTestCases(spec.data.map(() => false))}>
                <ViewList /> Collapse All
              </Button>
            )}

            {!isAllExpanded && (
              <Button
                color="primary"
                size="small"
                onClick={() => setExpandedTestCases(spec.data.map(() => true))}>
                <ViewStream /> Expand All
              </Button>
            )}
          </Box>
        </Grid>
      )}

      <Grid item xs={12} md={12}>
        {(() => {
          const testCases = [ ...spec.data ];
          testCases.sort((a, b) => {
            if (a.id !== undefined && b.id !== undefined) {
              if (a.id > b.id) return 1;
              if (a.id < b.id) return -1;
            }
            return 0;
          });

          if (!testCases.length) return (
            <Typography className="text-center">Empty spec defaults to a file submission</Typography>
          );

          return testCases.map((testCase, index) => (
            <Box pt={index > 0 ? 1.5 : 0} key={index}>
              <TestCasePanel
                summary={`Test Case ${index + 1}`}
                defaultExpanded={index === 0}
                expanded={Boolean(expandedTestCases[index])}
                onChange={(e, expanded) => {
                  setExpandedTestCases((state) => {
                    state[index] = expanded;
                    return [ ...state ];
                  });
                }}
              >
                <Grid container>
                  <Grid item xs={12}>
                    <TestCaseForm
                      data={testCase}
                      onChange={(data) => setTestCase(index, data)}
                      handleTestCaseChange={setTestCaseField}
                      handleRemoveInputVal={removeTestCaseInput}
                      index={index}
                    />
                  </Grid>

                  <Grid item className="d-flex justify-content-end w-100">
                    <Button
                      size="small"
                      color="secondary"
                      startIcon={<Delete />}
                      onClick={() => removeTestCase(index)}
                    >
                      Remove Test Case
                    </Button>
                  </Grid>
                </Grid>
              </TestCasePanel>
            </Box>
          ));
        })()}
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" justifyContent="center">
          <Button
            color="primary"
            startIcon={<Add />}
            onClick={() => addTestCase()}>
            Add a Test Case
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Simple;